import {
  equals,
  find,
  flatten,
  map,
  partial,
  pipe,
  pluck,
  propEq,
  uniq,
} from "ramda";
import { findById } from "helpers/collections";
import {
  type IBrandGroup,
  pluckEntityStores,
} from "helpers/transforms/entities";
import type { IEntity, IEntityApp } from "interfaces/app";

export const pluckBrandNames = (entities: IBrandGroup[]): string[] =>
  pluck("brand")(entities);

export const findAppFromStoreByAppName = (
  appName: string,
  store: IEntity
): IEntityApp | undefined => find(propEq("name", appName))(store.apps);

export const findStoreByStoreKey = (
  storeKey: string,
  brands: IBrandGroup[]
): IEntity | undefined => {
  return pipe(pluckEntityStores, partial(findById, [storeKey]))(brands);
};

export const findStoreByAppStoreKey = (
  appName: string,
  appStoreKey: string,
  stores: IEntity[]
): IEntity | undefined =>
  find((store) => {
    const app = findAppFromStoreByAppName(appName, store);
    return !!app && propEq("storeKey", appStoreKey, app);
  }, stores);

export const findBrandGroupByName = (
  name: string,
  brands: IBrandGroup[]
): IBrandGroup => find(propEq("brand", name))(brands);

export const findBrandGroupByBrandId = (
  brand: string,
  brands: IBrandGroup[]
): IBrandGroup | undefined => find(propEq("brandId", brand))(brands);

export const findBrandGroupByStoreKey = (
  storeKey: string,
  brands: IBrandGroup[]
): IBrandGroup | undefined => {
  const store: IEntity | undefined = findStoreByStoreKey(storeKey, brands);
  return store ? findBrandGroupByName(store.brandName, brands) : undefined;
};

export const findBrandGroupByStoreOrBrandKey = (
  vendorLevel: string,
  ambiguousKey: string,
  brands: IBrandGroup[]
): IBrandGroup | undefined => {
  if (equals(vendorLevel, "s")) {
    return findBrandGroupByStoreKey(ambiguousKey, brands);
  }
  return findBrandGroupByBrandId(ambiguousKey, brands);
};

export const getAppsInBrandGroups = (entities: IBrandGroup[]): string[] => {
  const allApps: any = uniq(
    flatten(
      map((brandGroup: IBrandGroup) => {
        return map((store: IEntity) => {
          return map((app: IEntityApp) => {
            return app.name === "swipely" ? "partners" : app.name;
          }, store.apps);
        }, brandGroup.stores);
      }, entities)
    )
  );
  return allApps || [];
};
